import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import InstallAppButton from "@/components/InstallAppButton";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BackButton from "@/components/BackButton";
import ActiveOrderBanner from "@/components/ActiveOrderBanner";
import NotificationBell from "@/components/NotificationBell";
import NotificationListener from "@/components/NotificationListener";
import { ShoppingBag, LogOut, User, MapPin, Bike, LayoutDashboard, UserCircle2 } from "lucide-react";

// Pages that ARE role-homes → no back button needed (there's nowhere to go back to).
const HOME_ROUTES = new Set(["/", "/welcome", "/login", "/admin", "/driver", "/restaurant"]);

export default function TopBar({ title, showCart = true, hideBack = false }) {
  const { user, logout } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const loc = useLocation();
  const showBack = !hideBack && !HOME_ROUTES.has(loc.pathname);

  const roleHome = user?.role === "driver" ? "/driver" : user?.role === "admin" ? "/admin" : "/";

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[var(--border)]" style={{boxShadow:"0 2px 10px rgba(44,40,37,0.03)"}}>
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3 gap-3">
        <div className="flex items-center gap-2">
          {showBack && <BackButton variant="icon"/>}
          <Link to={roleHome} className="flex items-center gap-2" data-testid="brand-home-link">
            <img src="/icon-192.png" alt="طلبية" className="w-10 h-10 rounded-xl object-cover" style={{boxShadow:"0 2px 6px rgba(37,71,208,0.25)"}} />
            <div className="leading-tight">
              <div className="font-black text-lg" style={{fontFamily:'Cairo', color:'var(--text-primary)'}}>طلبية</div>
              <div className="text-[10px] text-[var(--text-secondary)] -mt-1">Talabya</div>
            </div>
          </Link>
        </div>
        {title && <div className="hidden sm:block font-bold text-lg text-[var(--text-primary)]">{title}</div>}

        <div className="flex items-center gap-2">
          <LanguageSwitcher/>
          <InstallAppButton variant="pill" className="hidden md:inline-flex"/>
          {user?.role === "customer" && (
            <button
              onClick={() => navigate("/orders")}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-[var(--surface-alt)] text-sm font-bold text-[var(--text-primary)]"
              data-testid="nav-my-orders">
              <MapPin size={16} /> طلباتي
            </button>
          )}
          {user?.role === "customer" && (
            <button
              onClick={() => navigate("/profile")}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-xl hover:bg-[var(--surface-alt)] text-sm font-bold text-[var(--text-primary)]"
              data-testid="nav-profile"
              title="ملفي الشخصي">
              <UserCircle2 size={20} className="sm:w-4 sm:h-4"/>
              <span className="hidden sm:inline">ملفي</span>
            </button>
          )}
          {user?.role === "admin" && (
            <button
              onClick={() => navigate("/admin")}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-[var(--surface-alt)] text-sm font-bold"
              data-testid="nav-admin">
              <LayoutDashboard size={16} /> لوحة التحكم
            </button>
          )}
          {user?.role === "driver" && (
            <button
              onClick={() => navigate("/driver")}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-[var(--surface-alt)] text-sm font-bold"
              data-testid="nav-driver">
              <Bike size={16} /> طلبية مان
            </button>
          )}
          {showCart && user?.role === "customer" && (
            <button
              onClick={() => navigate("/cart")}
              className="relative flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary-brand text-white font-bold text-sm hover:opacity-90"
              data-testid="nav-cart">
              <ShoppingBag size={16} /> السلة
              {cart?.count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[var(--secondary)] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-black">
                  {cart.count}
                </span>
              )}
            </button>
          )}
          {user ? (
            <div className="flex items-center gap-1">
              <NotificationBell/>
              <div className="hidden md:flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[var(--surface-alt)]">
                <User size={14} className="text-[var(--text-secondary)]" />
                <span className="text-xs font-bold text-[var(--text-primary)]" data-testid="user-name">{user.name}</span>
              </div>
              <button
                onClick={async () => { await logout(); navigate("/login"); }}
                className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                title="تسجيل الخروج"
                data-testid="logout-btn">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link to="/login" className="px-4 py-2 rounded-xl bg-primary-brand text-white font-bold text-sm" data-testid="login-link">
              تسجيل الدخول
            </Link>
          )}
        </div>
      </div>
      {/* Sticky banner when the customer has an ACTIVE order — always accessible from any page */}
      <ActiveOrderBanner/>
      {/* Global WebSocket → in-app notifications listener */}
      <NotificationListener/>
    </header>
  );
}
